// Admin-side Deliverables panel: CA uploads final files for the order, user
// sees them on the Deliverables page (and on the completed-order summary).
//
// Reuses the existing /admin/orders/:orderId/steps/:stepNumber/upload
// endpoint (multer single-file upload, type prefixed with "admin_"). Files
// are listed via order.documents which is fetched on parent refresh.

import { useState } from "react";
import {
  CheckCircle2,
  Download,
  Eye,
  FileText,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import API from "../../lib/api";
import OfficePreview, { isOfficePreviewable } from "../OfficePreview";

function formatBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function timeAgo(ts) {
  if (!ts) return "";
  const sec = Math.max(1, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
  if (sec < 60)    return `${sec}s ago`;
  if (sec < 3600)  return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  return new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

async function fetchAdminDoc(docId, { inline = false } = {}) {
  const url = `/admin/documents/${docId}/download${inline ? "?inline=1" : ""}`;
  const { data } = await API.get(url, { responseType: "blob" });
  return URL.createObjectURL(data);
}

export default function DeliverablesUploadPanel({
  orderId, stepNumber, existingDocs = [], onRefresh,
}) {
  const [files, setFiles] = useState([]);          // queued, not yet uploaded
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: null });
  const [errors, setErrors] = useState([]);        // [{ name, message }]
  const [preview, setPreview] = useState(null);

  // Filter to admin uploads only — clients shouldn't be uploading to the
  // Deliverables step, but be defensive in case the data shape varies.
  const docs = (existingDocs || []).filter(
    (d) => (d.document_type || "").startsWith("admin_") ||
           d.document_type === "deliverable" || d.document_type === null,
  );

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    // Append, dedupe by name+size; cap at 20 to keep the UI sane.
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}`));
      const next = [...prev];
      for (const f of picked) {
        const key = `${f.name}|${f.size}`;
        if (!seen.has(key) && next.length < 20) {
          next.push(f);
          seen.add(key);
        }
      }
      return next;
    });
    // Clear the input so picking the same file again triggers onChange.
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onUpload = async () => {
    if (files.length === 0 || uploading) return;
    setUploading(true);
    setErrors([]);
    setProgress({ done: 0, total: files.length, current: files[0]?.name });

    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ done: i, total: files.length, current: f.name });
      try {
        const form = new FormData();
        form.append("deliverable", f);
        if (label.trim()) form.append("label", label.trim().slice(0, 200));
        await API.post(
          `/admin/orders/${orderId}/steps/${stepNumber}/upload`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } catch (e) {
        failed.push({ name: f.name, message: e.response?.data?.detail || "Upload failed" });
      }
    }

    setProgress({ done: files.length, total: files.length, current: null });
    // Keep failed files queued so the CA can retry; drop the successful ones.
    const failedNames = new Set(failed.map((x) => x.name));
    setFiles((prev) => prev.filter((f) => failedNames.has(f.name)));
    setErrors(failed);
    setLabel("");
    if (onRefresh) await onRefresh();
    setUploading(false);
  };

  const onPreview = async (doc) => {
    try {
      const url = await fetchAdminDoc(doc.id, { inline: true });
      setPreview({ url, name: doc.original_name, mime: doc.mime_type });
    } catch (e) {
      alert(e.response?.data?.detail || "Preview failed");
    }
  };

  const onDownload = async (doc) => {
    try {
      const url = await fetchAdminDoc(doc.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_name || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert(e.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-[#6C63FF]" />
        <h4 className="text-sm font-heading font-bold text-[#1A1A2E]">
          Deliverables — Files for Client
        </h4>
      </div>

      {/* Upload form */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs font-heading font-bold uppercase tracking-wide text-[#1A1A2E]">
            Upload Deliverables
          </p>
          {files.length > 0 && (
            <span className="text-[10px] font-body font-semibold text-[#6C63FF] bg-[#EEEDFC] px-2 py-0.5 rounded-full">
              {files.length} file{files.length === 1 ? "" : "s"} queued
            </span>
          )}
        </div>

        <label
          htmlFor={`deliverable-input-${stepNumber}`}
          className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-[#E8E6F8] hover:border-[#6C63FF]/40 rounded-xl py-6 cursor-pointer transition-colors bg-[#FAFAFE]"
        >
          <Upload className="h-5 w-5 text-[#6C63FF]" />
          <p className="text-xs font-body text-[#1A1A2E]">
            <span className="font-heading font-bold text-[#6C63FF]">Click to add files</span>
            {" "}or drag &amp; drop
          </p>
          <p className="text-[10px] text-[#9CA3AF] font-body">
            Select multiple at once · PDF, JPG, PNG, DOC, DOCX, XLS, XLSX · 10 MB each · up to 20 files
          </p>
          <input
            id={`deliverable-input-${stepNumber}`}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            onChange={onPickFiles}
            disabled={uploading}
            className="hidden"
          />
        </label>

        {/* Queued files */}
        {files.length > 0 && (
          <div className="space-y-1">
            {files.map((f, i) => (
              <div
                key={`${f.name}-${f.size}-${i}`}
                className="flex items-center gap-2 bg-[#FAFAFE] border border-[#E8E6F8] rounded-lg px-3 py-1.5"
              >
                <FileText className="h-3.5 w-3.5 text-[#6C63FF] flex-shrink-0" />
                <span className="text-xs font-body text-[#1A1A2E] truncate flex-1 min-w-0">
                  {f.name}
                </span>
                <span className="text-[10px] text-[#9CA3AF] font-body whitespace-nowrap">
                  {formatBytes(f.size)}
                </span>
                <button
                  onClick={() => removeFile(i)}
                  disabled={uploading}
                  className="text-[#9CA3AF] hover:text-red-500 disabled:opacity-30"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value.slice(0, 200))}
          placeholder="Optional label applied to every file in this batch (e.g. ITR-V, Acknowledgment)"
          disabled={uploading}
          className="w-full text-xs font-body border border-[#E8E6F8] rounded-lg p-2.5 bg-[#F5F4FB] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50 disabled:opacity-60"
        />

        {/* Per-batch progress */}
        {uploading && progress.total > 0 && (
          <div>
            <div className="flex items-center justify-between text-[11px] font-body text-[#6B7280] mb-1">
              <span className="truncate pr-2">
                Uploading <span className="font-mono">{progress.current}</span> · {progress.done + 1} of {progress.total}
              </span>
              <span className="font-heading font-bold text-[#6C63FF]">
                {Math.round((progress.done / progress.total) * 100)}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-[#F5F4FB] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] transition-all"
                style={{ width: `${(progress.done / progress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Per-file errors from last batch */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 space-y-1">
            <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-red-700">
              {errors.length} upload{errors.length === 1 ? "" : "s"} failed — fix and retry
            </p>
            {errors.map((er, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px] font-body text-red-600">
                <XCircle className="h-3 w-3 flex-shrink-0" />
                <span className="font-mono truncate">{er.name}</span>
                <span className="text-red-500/80">— {er.message}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-[10px] text-[#9CA3AF] font-body">
            {label.length}/200 chars
          </span>
          <button
            onClick={onUpload}
            disabled={files.length === 0 || uploading}
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading
              ? `Uploading ${progress.done + 1}/${progress.total}…`
              : `Upload ${files.length || ""} file${files.length === 1 ? "" : "s"}`.trim()}
          </button>
        </div>
      </div>

      {/* Existing uploads */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-heading font-bold uppercase tracking-wide text-[#1A1A2E]">
            Uploaded Files ({docs.length})
          </p>
          {docs.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-body font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
              <CheckCircle2 className="h-3 w-3" /> Visible to client
            </span>
          )}
        </div>
        {docs.length === 0 ? (
          <p className="text-center text-xs text-[#9CA3AF] font-body py-6 bg-white border border-dashed border-[#E8E6F8] rounded-xl">
            No deliverables uploaded yet. The client will see whatever you upload here.
          </p>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex items-center gap-3 bg-white border border-[#E8E6F8] rounded-xl px-3 py-2 flex-wrap"
              >
                <div className="w-9 h-9 rounded-lg bg-[#EEEDFC] flex items-center justify-center flex-shrink-0">
                  <FileText className="h-4 w-4 text-[#6C63FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-body text-[#1A1A2E] truncate">
                    {d.original_name}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF] font-body">
                    {formatBytes(d.file_size)} · uploaded {timeAgo(d.uploaded_at || d.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onPreview(d)}
                    className="inline-flex items-center gap-1 text-[11px] font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#EEEDFC] rounded-lg px-2 py-1"
                  >
                    <Eye className="h-3 w-3" /> Preview
                  </button>
                  <button
                    onClick={() => onDownload(d)}
                    className="inline-flex items-center gap-1 text-[11px] font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#EEEDFC] rounded-lg px-2 py-1"
                  >
                    <Download className="h-3 w-3" /> Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E6F8]">
              <p className="text-sm font-heading font-bold text-[#1A1A2E] truncate pr-4">{preview.name}</p>
              <div className="flex items-center gap-2">
                <a href={preview.url} download={preview.name} className="inline-flex items-center gap-1 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#EEEDFC] rounded-lg px-2.5 py-1.5">
                  <Download className="h-3 w-3" /> Download
                </a>
                <button onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} className="p-1.5 rounded-lg hover:bg-[#F5F4FB] text-[#6B7280]">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[#F5F4FB]">
              {preview.mime?.startsWith("image/") ? (
                <img src={preview.url} alt={preview.name} className="max-w-full max-h-full mx-auto" />
              ) : preview.mime === "application/pdf" ? (
                <iframe src={preview.url} title={preview.name} className="w-full h-[80vh] border-0" />
              ) : isOfficePreviewable({ mime: preview.mime, name: preview.name }) ? (
                <OfficePreview url={preview.url} mime={preview.mime} name={preview.name} />
              ) : (
                <div className="p-8 text-center text-sm text-[#6B7280] font-body">
                  <p className="mb-3">This file type can't be previewed in-window.</p>
                  <a href={preview.url} download={preview.name} className="inline-flex items-center gap-1.5 text-sm font-body font-semibold text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#EEEDFC] rounded-lg px-3 py-2">
                    <Download className="h-4 w-4" /> Download to view
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
